import type { CSSObject } from 'tss-react'
import { makeStyles } from 'tss-react/mui'
// Adapted from @react-admin/ra-calendar@1.2.0 — see ../README.md
//
// FullCalendar 6 merged @fullcalendar/common into core, which retired v5's
// "import the top-level lib before any plugin" rule. The load-order pinning
// this file used to need is gone with it.
import type {
  DateSelectArg,
  DatesSetArg,
  EventChangeArg,
  EventClickArg,
  EventInput,
} from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import FullCalendar from '@fullcalendar/react'
import timeGridPlugin from '@fullcalendar/timegrid'
import isEqual from 'lodash/isEqual'
import { ComponentProps, useEffect, useRef } from 'react'
import {
  OnError,
  OnSuccess,
  sanitizeListRestProps,
  TransformData,
  useListContext,
  useNotify,
  useRefresh,
  useSidebarState,
  useUpdate,
} from 'react-admin'
import { useNavigate } from 'react-router-dom'
import { convertToEvent as defaultConvertToEvent } from './convertToEvent'
import { getFilterValuesFromInterval as defaultGetFilterValuesFromInterval } from './getFilterValuesFromInterval'

/**
 * Calendar view, based on FullCalendar's <FullCalendar> component.
 *
 * Uses the react-admin list context as its content provider, and links to the
 * edit and create views of the current resource. Must be used inside a
 * <ListContext>, preferably as a child of <List>. Additional props are passed
 * through to <FullCalendar>, which is how the caller overrides the defaults
 * below — note the spread comes last.
 */
export const Calendar = (props: CalendarProps) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const navigate = useNavigate()

  const defaultOnSuccess = () => {
    notify('ra.notification.updated', {
      type: 'info',
      messageArgs: { smart_count: 1 },
      undoable: true,
    })
  }
  const defaultOnFailure = (error) => {
    notify(typeof error === 'string' ? error : error.message || 'ra.notification.http_error', {
      type: 'warning',
    })
    refresh()
  }

  const {
    getFilterValueFromInterval = defaultGetFilterValuesFromInterval,
    convertToEvent = defaultConvertToEvent,
    onSuccess = defaultOnSuccess,
    onFailure = defaultOnFailure,
    transform,
    ...rest
  } = props

  const { classes } = useStyles()
  const { data, filterValues, setFilters, displayedFilters, resource } = useListContext()
  const [updateEvent] = useUpdate()
  const calendar = useRef<any>(null)

  // Update size when the sidebar opens or closes.
  const isFirstRender = useRef(true)
  useEffect(() => {
    isFirstRender.current = false
  }, [])
  const [sidebarIsOpen] = useSidebarState()
  useEffect(() => {
    if (!isFirstRender.current) {
      setTimeout(() => {
        // @ts-ignore
        calendar.current?.getApi().updateSize()
      }, 300) // the max duration of the sidebar transition
    }
  }, [sidebarIsOpen])

  const handleDatesSet = (dateInfo: DatesSetArg): void => {
    const newFilterValues = getFilterValueFromInterval(dateInfo, filterValues)
    if (!isEqual(filterValues, newFilterValues)) {
      setFilters(newFilterValues, displayedFilters)
    }
  }

  const handleDateSelect = (selectInfo: DateSelectArg): void => {
    const calendarApi = selectInfo.view.calendar
    calendarApi.unselect() // clear date selection
    navigate(`/${resource}/create`, {
      state: {
        record: {
          start: selectInfo.startStr,
          end: selectInfo.endStr,
          allDay: selectInfo.allDay,
        },
      },
    })
  }

  const handleEventClick = (clickInfo: EventClickArg): void => {
    navigate(`/${resource}/${clickInfo.event.id}`)
  }

  const handleEventChange = async (changeInfo: EventChangeArg) => {
    const payload = {
      id: changeInfo.event.id,
      data: transform
        ? await transform(changeInfo.event.toPlainObject())
        : changeInfo.event.toPlainObject(),
    }
    // react-admin 4's useUpdate takes (resource, { id, data }, options); the
    // v3 `payload` wrapper and `undoable` flag are gone (mutationMode replaces it).
    updateEvent(
      resource,
      { id: payload.id, data: payload.data },
      { onSuccess, onError: onFailure, mutationMode: 'undoable' }
    )
  }

  return (
    <div className={classes.root}>
      <FullCalendar
        ref={calendar}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        initialView='dayGridMonth'
        editable={true}
        selectable={true}
        selectMirror={true}
        dayMaxEvents={true}
        navLinks={true}
        datesSet={handleDatesSet}
        select={handleDateSelect}
        events={(data ?? []).filter(Boolean).map(convertToEvent)}
        eventClick={handleEventClick}
        eventChange={handleEventChange} // called for drag-n-drop/resize
        {...sanitizeListRestProps(rest)}
      />
    </div>
  )
}

const useStyles = makeStyles({ name: 'RaCalendar' })((theme) => ({
  root: {
    padding: '1em',
    // material-ui skin for full calendar
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.primary,
    '& .fc-toolbar-title': {
      fontSize: theme.typography.h5.fontSize,
      fontWeight: theme.typography.h5.fontWeight,
    },
    '& .fc-button': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.action.active,
      borderColor: theme.palette.divider,
      fontSize: theme.typography.button.fontSize,
      fontWeight: theme.typography.button.fontWeight,
      textTransform: theme.typography.button.textTransform as CSSObject['textTransform'],
    },
    '& .fc-button-primary:disabled': {
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.action.selected,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      color: theme.palette.action.active,
    },
    '& .fc-button-group .fc-button': {
      borderColor: theme.palette.divider,
      color: theme.palette.action.active,
    },
    '& .fc-button:hover': {
      borderColor: theme.palette.divider,
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
    '& .fc-button-primary:not(:disabled):active': {
      backgroundColor: theme.palette.action.active,
      color: theme.palette.text.primary,
    },
    '& .fc-button-group .fc-button-primary:not(:disabled):active': {
      borderColor: theme.palette.divider,
    },
    '& .fc-button-primary:not(:disabled).fc-button-active': {
      backgroundColor: theme.palette.action.selected,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      color: theme.palette.action.active,
    },
    '& .fc-button-group .fc-button-primary:not(:disabled).fc-button-active': {
      borderColor: theme.palette.divider,
      color: theme.palette.text.secondary,
    },
    '& .fc-button-primary:not(:disabled):focus': {
      boxShadow: 'none',
    },
    '& .fc-button-primary:not(:disabled):active:focus': {
      boxShadow: 'none',
    },
    '& .fc-button-primary:not(:disabled).fc-button-active:focus': {
      boxShadow: 'none',
    },
    '& .fc-popover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}))

export interface CalendarProps extends ComponentProps<typeof FullCalendar> {
  getFilterValueFromInterval?: (dateInfo: DatesSetArg, filterValues: any) => any
  convertToEvent?: (event: any) => EventInput
  onSuccess?: OnSuccess
  onFailure?: OnError
  transform?: TransformData
}

export default Calendar
